import { useGetLeaseCompsQuery } from "@/services/leaseComps";
import { useSurvey } from "@hooks/useSurveySelectors";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetMediaByIdsQuery } from "@services/media";
import { useGetPolygonsQuery } from "@services/survey-polygons";
import { useGetSurveyQuery } from "@services/survey-core";
import FloorPlanPreviewRenderer from "@survey-components/pdf-previews/FloorPlanPreviewRenderer";
import SectionRenderer from "@survey-components/pdf-previews/SectionRenderer";
import { SurveySection } from "@survey-types/survey.section.types";
import { isUSUser } from "@utils/countryUtils";
import { useParams } from "react-router";

const SectionPreview = () => {
  const {
    surveyId,
    id, // For the floor plan route where surveyId is called 'id'
    section: sectionParam,
    propertyId,
    index,
  } = useParams();

  // Use surveyId or id (for floor plan route)
  const activeSurveyId = surveyId || id;

  // Fetch survey to populate Redux store
  const { isLoading, isError, error } = useGetSurveyQuery(
    activeSurveyId ?? skipToken,
  );

  // Fetch polygons for map rendering in preview routes
  useGetPolygonsQuery(activeSurveyId || "", {
    skip: !activeSurveyId,
    refetchOnMountOrArgChange: false,
  });

  // Get survey from Redux store (populated by RTK Query)
  const survey = useSurvey();

  // Check if this is a floor plan image preview
  const isFloorPlanImagePreview = Boolean(
    propertyId && index && !isNaN(Number(index)),
  );

  const targetSection = survey?.sections?.find((section: SurveySection) => {
    const sectionName = section.name?.replace(/\s/g, "").toLowerCase()?.trim();
    return (
      sectionName === sectionParam?.toLowerCase() ||
      (sectionParam === "renderedMarketMap" && sectionName === "marketmap") ||
      (sectionParam === "leasecompscomparison" && sectionName === "leasecomps")
    );
  });

  // Check if this is a property comparison section
  const isPropertyComparison =
    targetSection?.name?.replace(/\s/g, "").toLowerCase()?.trim() ===
    "propertycomparison";

  // Check if this is a lease comps comparison section
  const isLeaseCompsComparison =
    sectionParam?.toLowerCase() === "leasecompscomparison" && isUSUser();

  // Fetch lease comps data if this is a lease comps comparison
  const { data: leaseComps } = useGetLeaseCompsQuery(
    isLeaseCompsComparison && activeSurveyId ? activeSurveyId : skipToken,
  );

  // Fetch media for floor plan images or all properties in property comparison
  const propertyIds = propertyId
    ? [propertyId]
    : isPropertyComparison
      ? survey?.properties?.map((prop) => prop.propertyId) || []
      : [];

  // Fetch lease comp IDs for lease comps comparison
  const leaseCompIds = isLeaseCompsComparison
    ? (leaseComps || [])
        .filter((lc) => !lc.isHidden)
        .map((lc) => lc.leaseCompId)
    : [];

  // Fetch media data when viewing floor plan images, property comparison, or lease comps comparison
  const {
    isLoading: isMediaLoading,
    isError: isMediaError,
    error: mediaError,
  } = useGetMediaByIdsQuery(
    propertyIds.length > 0
      ? { ids: propertyIds, ownerType: "PROPERTY" }
      : leaseCompIds.length > 0
        ? { ids: leaseCompIds, ownerType: "LEASE_COMP" }
        : skipToken,
  );

  // Helper to format error messages consistently
  const formatErrorMessage = (error: unknown): string | null => {
    if (!error) return null;
    if ("status" in (error as object)) {
      return "error" in (error as object)
        ? (error as { error: string }).error
        : JSON.stringify((error as { data: unknown }).data);
    }
    return (error as { message?: string }).message || "Unknown error";
  };

  // Helper to render error display
  const renderError = (title: string, error: unknown) => (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-red-600 mb-2">{title}</h2>
        <p className="text-gray-600">{formatErrorMessage(error)}</p>
      </div>
    </div>
  );

  if (isError) return renderError("Error Loading Survey", error);
  if (isFloorPlanImagePreview && isMediaError) {
    return renderError("Error Loading Media", mediaError);
  }

  const isAnyLoading =
    isLoading ||
    (isFloorPlanImagePreview && isMediaLoading) ||
    (isPropertyComparison && isMediaLoading) ||
    (isLeaseCompsComparison && isMediaLoading);

  if (isAnyLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading survey...</p>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600 mb-2">
            Survey Not Found
          </h2>
          <p className="text-gray-500">
            The requested survey could not be found.
          </p>
        </div>
      </div>
    );
  }

  // Handle floor plan image preview (special case)
  if (isFloorPlanImagePreview) {
    return <FloorPlanPreviewRenderer propertyId={propertyId} index={index} />;
  }

  if (!targetSection) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600 mb-2">
            Section Not Found
          </h2>
          <p className="text-gray-500">
            The section &ldquo;{sectionParam}&rdquo; could not be found in this
            survey.
          </p>
          <div className="mt-4">
            <p className="text-sm text-gray-400">Available sections:</p>
            <ul className="text-sm text-gray-500 mt-2">
              {survey.sections?.map((section: SurveySection) => (
                <li key={section.surveySectionId}>
                  {section.name} (
                  {section.name?.replace(/\s/g, "").toLowerCase()})
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section-preview-container min-h-screen bg-gray-50">
      <div className="flex flex-col items-center">
        <SectionRenderer section={targetSection} propertyId={propertyId} />
      </div>
    </div>
  );
};

export default SectionPreview;
